import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { ListRow } from '../api/types';
import type { View } from '../lib/views';
import { VIEWS, viewKey } from '../lib/views';
import { useAuthStore } from '../store/auth';

interface Props {
  lists: ListRow[];
  currentView: View;
  onSelect: (v: View) => void;
  onCreateList: (name: string) => Promise<void>;
  onDeleteList: (id: string) => Promise<void>;
  /** 移动端 drawer 开关状态, 仅 mobile (<md) 生效 */
  open: boolean;
  onClose: () => void;
}

export function ListSidebar({
  lists,
  currentView,
  onSelect,
  onCreateList,
  onDeleteList,
  open,
  onClose,
}: Props) {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const clear = useAuthStore((s) => s.clear);

  const fixedItems: { view: View; icon: string; label: string; badge?: string }[] = [
    { view: VIEWS.myDay, icon: '☀️', label: '我的一天' },
    { view: VIEWS.important, icon: '⭐', label: '重要' },
    { view: VIEWS.all, icon: '📋', label: '所有任务' },
    { view: VIEWS.completed, icon: '✓', label: '已完成' },
    { view: VIEWS.archived, icon: '🗄', label: '已归档' },
  ];

  const handleCreate = async () => {
    if (!newName.trim()) {
      setCreating(false);
      return;
    }
    await onCreateList(newName.trim());
    setNewName('');
    setCreating(false);
  };

  // 选择项目后, 移动端关闭 drawer
  const handleSelect = (v: View) => {
    onSelect(v);
    onClose();
  };
  const handleCreateAndClose = async () => {
    await handleCreate();
    onClose();
  };

  return (
    <>
      {/* Backdrop (仅 mobile) */}
      <div
        onClick={onClose}
        className={`fixed inset-0 z-30 bg-black/40 transition-opacity md:hidden ${
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden="true"
      />

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-gray-200 bg-white transition-transform duration-200 ease-out dark:border-gray-700 dark:bg-gray-800 md:static md:translate-x-0 safe-top md:safe-top-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* 顶部 */}
        <div className="flex items-center justify-between px-4 py-3 safe-top">
          <h1 className="text-lg font-semibold">Todo</h1>
          {/* 移动端关闭按钮 */}
          <button
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 md:hidden"
            aria-label="关闭侧边栏"
          >
            ✕
          </button>
        </div>

        <nav className="flex-1 space-y-0.5 overflow-y-auto px-2">
          {fixedItems.map((item) => (
            <button
              key={viewKey(item.view)}
              onClick={() => handleSelect(item.view)}
              className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition ${
                viewKey(currentView) === viewKey(item.view)
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <span className="text-base">{item.icon}</span>
              <span className="flex-1">{item.label}</span>
              {item.badge && (
                <span className="text-xs text-gray-400">{item.badge}</span>
              )}
            </button>
          ))}

          <div className="mt-4 mb-1 flex items-center justify-between px-2 text-xs font-medium uppercase text-gray-400">
            <span>列表</span>
            <button
              onClick={() => setCreating(true)}
              className="flex items-center gap-1 rounded px-2 py-0.5 text-xs font-normal normal-case text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
              title="新建列表"
            >
              <span className="text-base leading-none">+</span>
              <span>新建</span>
            </button>
          </div>

          {creating && (
            <div className="px-2 py-1">
              <input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreateAndClose();
                  if (e.key === 'Escape') {
                    setCreating(false);
                    setNewName('');
                  }
                }}
                onBlur={handleCreate}
                placeholder="列表名"
                className="w-full rounded border border-blue-400 bg-white px-2 py-1 text-sm outline-none dark:bg-gray-700"
              />
            </div>
          )}

          {lists.map((l) => (
            <div
              key={l.id}
              className={`group flex items-center gap-2 rounded px-2 py-1.5 text-sm transition ${
                viewKey(currentView) === `list:${l.id}`
                  ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <button
                onClick={() =>
                  handleSelect({ kind: 'list', listId: l.id })
                }
                className="flex flex-1 items-center gap-2 text-left"
              >
                <span>{l.icon ?? '📁'}</span>
                {l.color && (
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ background: l.color }}
                  />
                )}
                <span className="flex-1 truncate">{l.name}</span>
              </button>
              <button
                onClick={() => {
                  if (confirm(`删除列表 "${l.name}" 及其所有任务?`)) {
                    onDeleteList(l.id);
                  }
                }}
                className="text-xs text-gray-400 hover:text-red-500 md:hidden md:group-hover:block"
                title="删除"
              >
                ✕
              </button>
            </div>
          ))}
        </nav>

        {/* 底部: 设置 + 登出 (永远可见) */}
        <div className="border-t border-gray-200 px-2 py-2 dark:border-gray-700 safe-bottom">
          <Link
            to="/settings"
            onClick={onClose}
            className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-gray-600 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <span>⚙️</span>
            <span>设置</span>
          </Link>
          <button
            onClick={() => {
              clear();
              onClose();
            }}
            className="mt-0.5 flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-gray-600 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            <span>⎋</span>
            <span>登出</span>
          </button>
        </div>
      </aside>
    </>
  );
}
